import React, { useEffect, useState, useCallback, useMemo } from "react";
import axios from "axios";
import ReactApexChart from "react-apexcharts";
import { useAuth } from "../../../services/useAuth";
import { useTitle } from "../../../hooks/useTitle";
import { ToastContainer, Toast } from "../../../components/CustomUI/Toast";
import AppPage from "../../../components/CustomUI/AppPage";
import CardContainer from "../../../components/CustomUI/CardContainer";
import Button from "../../../components/BaseUI/Button";
import Label from "../../../components/BaseUI/Label";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "../../../components/BaseUI/Modal";
import { Table, Tr, Td } from "../../../components/BaseUI/Table";
import { FiInfo, FiRefreshCw } from "react-icons/fi";
import { withAuth } from "../../../components/HOC/withAuth";
import ReportViewOptionsPanel from "../Reports/shared/ReportViewOptionsPanel";
import { useReportFilters } from "../Reports/shared/useReportFilters";
import {
  SectionSpinner,
  SectionEmpty,
  fmtPct,
  fmtMins,
  toISO,
} from "../Reports/shared/utils";
import { API_URL } from "../Reports/shared/constants";
import "../Reports/Reports.scss";
import "./TEEP.scss";

const fmtLocalDT = (isoStr) => {
  const d = new Date(isoStr);
  const p = (n) => String(n).padStart(2, "0");
  return `${p(d.getDate())}.${p(d.getMonth() + 1)}.${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}`;
};

const fmtInt = (n) => Math.round(Number(n) || 0).toLocaleString("tr-TR");

const WaterfallChart = ({ result, filters, machines }) => {
  const [summaryOpen, setSummaryOpen] = useState(false);
  const { time, metrics, period } = result;

  const calendar = time.calendarMinutes;
  const planned = time.plannedMinutes;
  const stopM = time.stopMinutes;
  const available = time.availableMinutes;

  const tatilMin = Math.max(0, calendar - planned);
  const theoreticalMin = Math.round((available * metrics.performance) / 100);
  const goodTheoreticalMin = Math.round(
    (theoreticalMin * metrics.quality) / 100,
  );
  const perfLoss = Math.max(0, available - theoreticalMin);
  const qualityLoss = Math.max(0, theoreticalMin - goodTheoreticalMin);

  const machineLabel =
    filters.machineIds.length > 0
      ? machines
          .filter((m) =>
            filters.machineIds.some((id) => String(id) === String(m._id)),
          )
          .map((m) => m.name)
          .join(", ")
      : "Toplam";

  const chartTitle = `[TEEP Şelalesi]  [${fmtLocalDT(period.startDate)} .. ${fmtLocalDT(period.endDate)}]  [${machineLabel}]`;

  const categories = [
    "Toplam Süre",
    "Tatil",
    "Çalışılabilir Süre",
    ["Uzun", "Duruş"],
    "Çalışılan Süre",
    ["Küçük Duruş/", "Hız Düşümü"],
    ["Toplam", "Teorik Süre"],
    ["Sağlam", "Teorik Süre"],
  ];

  /** Semantic value shown in tooltip / table per column */
  const displayMinutes = [
    calendar,
    tatilMin,
    planned,
    stopM,
    available,
    perfLoss,
    theoreticalMin,
    goodTheoreticalMin,
  ];

  const BAR_BLUE = "#3d4fa8";
  const BAR_ORANGE = "#ea580c";
  const BAR_RED = "#dc2626";

  const kpiCells = [
    null,
    null,
    // { lines: [`Kapasite: ${metrics.utilization}%`] },
    null,
    null,
    // { lines: [`Kullanılab.: ${metrics.availability}%`] },
    // { lines: [`Performans: ${metrics.performance}%`] },
    // { lines: [`Performans: ${metrics.performance}%`] },
    // {
    //   lines: [
    //     `Kalite: ${metrics.quality}%`,
    //     `OEE: ${metrics.oee}%`,
    //     `TEEP: ${metrics.teep}%`,
    //   ],
    // },
    null,
    null,
    null,
    null,
  ];

  const summaryRows = useMemo(
    () => [
      {
        stage: "Toplam süre",
        mins: calendar,
        note: "Seçilen aralığın takvim süresi (bitiş − başlangıç).",
      },
      {
        stage: "Tatil / takvim dışı",
        mins: tatilMin,
        note: "Takvim − planlı süre (vardiya planı dışındaki zaman).",
      },
      {
        stage: "Çalışılabilir süre (planlı)",
        mins: planned,
        note: `Kapasite kullanımı = planlı / takvim → %${metrics.utilization}.`,
      },
      {
        stage: "Uzun duruş (toplam duruş)",
        mins: stopM,
        note: "Kayıtlı tüm duruşlar (OEE sınıfına göre dağılım aşağıdaki grafikte).",
      },
      {
        stage: "Çalışılan süre",
        mins: available,
        note: `Planlı − duruş. Kullanılabilirlik = çalışılan / planlı → %${metrics.availability}.`,
      },
      {
        stage: "Küçük duruş / hız düşümü (türetilmiş)",
        mins: perfLoss,
        note: `Çalışılan − teorik net. Performans = (ideal çevrim × adet toplamı) / çalışılan → %${metrics.performance}.`,
      },
      {
        stage: "Toplam teorik süre",
        mins: theoreticalMin,
        note: "Çalışılan × performans% (yuvarlanmış).",
      },
      {
        stage: "Sağlam teorik süre",
        mins: goodTheoreticalMin,
        note: `Teorik net × kalite%. OEE = A×P×K; TEEP = OEE × kapasite% → OEE %${metrics.oee}, TEEP %${metrics.teep}.`,
      },
    ],
    [
      calendar,
      tatilMin,
      planned,
      stopM,
      available,
      perfLoss,
      theoreticalMin,
      goodTheoreticalMin,
      metrics.utilization,
      metrics.availability,
      metrics.performance,
      metrics.quality,
      metrics.oee,
      metrics.teep,
    ],
  );

  const yMax = useMemo(() => {
    const maxVal = Math.max(calendar, planned, available, theoreticalMin, 1);
    return maxVal <= 600
      ? Math.ceil(maxVal / 50) * 50
      : Math.ceil(maxVal / 500) * 500;
  }, [calendar, planned, available, theoreticalMin]);

  const series = useMemo(() => {
    const sp = [0, planned, 0, available, 0, theoreticalMin, 0, 0];
    const tb = [
      calendar,
      0,
      planned,
      0,
      available,
      0,
      theoreticalMin,
      goodTheoreticalMin,
    ];
    const lr = [0, tatilMin, 0, stopM, 0, 0, 0, 0];
    const lo = [0, 0, 0, 0, 0, perfLoss, 0, qualityLoss];
    return [
      { name: " ", data: sp },
      { name: "Süre", data: tb },
      { name: "Planlı kayıp", data: lr },
      { name: "Hız / kalite kaybı", data: lo },
    ];
  }, [
    calendar,
    planned,
    available,
    theoreticalMin,
    goodTheoreticalMin,
    tatilMin,
    stopM,
    perfLoss,
    qualityLoss,
  ]);

  const chartOptions = useMemo(() => {
    const tipMeta = [
      null,
      null,
      `Kapasite kullanımı: ${metrics.utilization}%`,
      null,
      `Kullanılabilirlik: ${metrics.availability}%`,
      `Performans: ${metrics.performance}%`,
      `Performans: ${metrics.performance}%`,
      `Performans: ${metrics.performance}%`,
      `Kalite: ${metrics.quality}% · OEE: ${metrics.oee}% · TEEP: ${metrics.teep}%`,
    ];
    return {
      chart: {
        id: "teep-waterfall",
        type: "bar",
        stacked: true,
        stackType: "normal",
        fontFamily: "inherit",
        foreColor: "#334155",
        toolbar: {
          show: true,
          offsetY: -4,
          tools: {
            download: true,
            selection: false,
            zoom: false,
            zoomin: false,
            zoomout: false,
            pan: false,
            reset: false,
          },
        },
        animations: { enabled: true, easing: "easeinout", speed: 550 },
      },
      title: {
        text: chartTitle,
        align: "center",
        margin: 12,
        offsetY: 4,
        style: { fontSize: "12px", fontWeight: 600, color: "#334155" },
      },
      plotOptions: {
        bar: {
          horizontal: false,
          columnWidth: "58%",
          borderRadius: 4,
          borderRadiusApplication: "end",
          borderRadiusWhenStacked: "last",
          dataLabels: { position: "center", hideOverflowingLabels: true },
        },
      },
      colors: ["rgba(0,0,0,0)", BAR_BLUE, BAR_RED, BAR_ORANGE],
      states: {
        hover: { filter: { type: "darken", value: 0.08 } },
        active: { filter: { type: "none", value: 0 } },
      },
      xaxis: {
        categories,
        tickPlacement: "between",
        axisBorder: { show: true, color: "#e2e8f0" },
        axisTicks: { show: false },
        labels: {
          style: { fontSize: "11px", fontWeight: 600, colors: "#475569" },
          trim: false,
          maxHeight: 72,
        },
      },
      yaxis: {
        max: yMax,
        min: 0,
        tickAmount: 5,
        title: {
          text: "Dakika",
          rotate: -90,
          offsetX: 4,
          style: { fontSize: "12px", fontWeight: 600, color: "#64748b" },
        },
        labels: {
          formatter: (v) => Math.round(v).toLocaleString("tr-TR"),
          style: { fontSize: "11px", colors: "#64748b" },
        },
      },
      stroke: { show: false },
      dataLabels: {
        enabled: true,
        enabledOnSeries: [1, 2, 3],
        textAnchor: "middle",
        formatter: (val, opts) => {
          const v = typeof val === "number" ? val : Number(val);
          if (!v || v <= 0) return "";
          const si = opts.seriesIndex;
          const di = opts.dataPointIndex ?? 0;
          let labelVal = v;
          if (si === 1 && [0, 2, 4, 6, 7].includes(di))
            labelVal = displayMinutes[di];
          else if (si === 2 && [1, 3].includes(di))
            labelVal = displayMinutes[di];
          else if (si === 3 && di === 5) labelVal = displayMinutes[5];
          else if (si === 3 && di === 7) labelVal = qualityLoss;
          if (yMax > 0 && labelVal / yMax < 0.04 && labelVal > 0) return "";
          return fmtInt(labelVal);
        },
        offsetY: 0,
        style: {
          fontSize: "11px",
          fontWeight: 700,
          colors: ["#ffffff"],
        },
        dropShadow: {
          enabled: true,
          top: 0,
          left: 0,
          blur: 2,
          opacity: 0.35,
          color: "#000000",
        },
      },
      legend: { show: false },
      tooltip: {
        theme: "dark",
        intersect: false,
        shared: true,
        followCursor: true,
        fillSeriesColor: false,
        style: { fontSize: "12px" },
        custom({ dataPointIndex }) {
          const v = displayMinutes[dataPointIndex];
          const meta = tipMeta[dataPointIndex];
          const num = fmtInt(v);
          const cat = Array.isArray(categories[dataPointIndex])
            ? categories[dataPointIndex].join(" ")
            : categories[dataPointIndex];
          const metaHtml = meta
            ? `<div class="teep-wf-tip__meta">${meta}</div>`
            : "";
          return `
            <div class="teep-wf-tip">
              <div class="teep-wf-tip__cat">${cat}</div>
              <div class="teep-wf-tip__val">${num} <span>dk</span></div>
              ${metaHtml}
            </div>`;
        },
      },
      grid: {
        borderColor: "#e8eaed",
        strokeDashArray: 3,
        padding: { left: 8, right: 10, top: 4 },
        xaxis: { lines: { show: true } },
        yaxis: { lines: { show: true } },
      },
    };
  }, [
    chartTitle,
    categories,
    yMax,
    displayMinutes,
    qualityLoss,
    metrics.utilization,
    metrics.availability,
    metrics.performance,
    metrics.quality,
    metrics.oee,
    metrics.teep,
  ]);

  return (
    <div className="teep-waterfall-wrap">
      <CardContainer>
        <div className="d-flex justify-content-between align-items-center pt-3 ml-3">
          <h3> TEEP Şelalesi</h3>
          <div className="d-flex justify-content-end mr-4">
            <button
              type="button"
              className="teep-wf-summary-info-btn"
              onClick={() => setSummaryOpen(true)}
              title="Hesaplama özeti"
              aria-label="Hesaplama özeti — açıklamaları göster"
            >
              <FiInfo size={20} aria-hidden />
            </button>
          </div>
        </div>
        <div className="teep-wf-kpi-grid" aria-hidden={false}>
          {kpiCells.map((cell, i) => (
            <div key={i} className="teep-wf-kpi-cell">
              {cell?.lines?.map((line, j) => (
                <span key={j} className="teep-wf-kpi-pill">
                  {line}
                </span>
              ))}
            </div>
          ))}
        </div>

        <div className="teep-wf-chart-with-info">
          <div className="teep-wf-chart-canvas">
            <ReactApexChart
              type="bar"
              height={480}
              series={series}
              options={chartOptions}
            />
          </div>
        </div>

        <Modal
          isOpen={summaryOpen}
          onClose={() => setSummaryOpen(false)}
          width="large"
          scrollBehavior="inside"
        >
          <ModalHeader>Hesaplama özeti</ModalHeader>
          <ModalBody>
            <p className="text-muted small mb-3">
              Şelale sütunlarındaki dakika değerleri ve yüzdelerin nasıl
              türetildiği.
            </p>
            <div className="table-responsive">
              <table className="table table-sm teep-wf-summary-table mb-0">
                <thead>
                  <tr>
                    <th>Aşama</th>
                    <th className="text-end">Dakika</th>
                    <th>Açıklama</th>
                  </tr>
                </thead>
                <tbody>
                  {summaryRows.map((row) => (
                    <tr key={row.stage}>
                      <td>{row.stage}</td>
                      <td className="text-end text-nowrap fw-semibold">
                        {fmtInt(row.mins)}
                      </td>
                      <td className="small text-muted">{row.note}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </ModalBody>
          <ModalFooter>
            <Button
              type="button"
              appearance="default"
              onClick={() => setSummaryOpen(false)}
            >
              Kapat
            </Button>
          </ModalFooter>
        </Modal>
      </CardContainer>
    </div>
  );
};
const TEEP = () => {
  useTitle("Rovion MES - TEEP Analizi");
  const { getToken } = useAuth();
  const filters = useReportFilters();

  const [performance, setPerformance] = useState("100");
  const [quality, setQuality] = useState("100");
  const [machines, setMachines] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [toasts, setToasts] = useState([]);

  const dismissToast = useCallback(
    (id) => setToasts((p) => p.filter((t) => t.id !== id)),
    [],
  );
  const addToast = useCallback((msg, type = "success") => {
    setToasts((p) => [...p, { id: Date.now(), message: msg, type }]);
  }, []);
  const authH = useCallback(
    () => ({ headers: { Authorization: `Bearer ${getToken()}` } }),
    [getToken],
  );

  useEffect(() => {
    (async () => {
      try {
        const [mR, sR] = await Promise.all([
          axios.get(`${API_URL}/api/machines`, authH()),
          axios.get(`${API_URL}/api/shifts`, authH()),
        ]);
        setMachines(mR.data.machines || mR.data || []);
        setShifts(sR.data.shifts || []);
      } catch {
        /* ignore */
      }
    })();
  }, [authH]);

  const runCalculation = useCallback(async () => {
    setLoading(true);
    setResult(null);
    try {
      const p = new URLSearchParams();
      p.set("startDate", toISO(filters.startDate, false));
      p.set("endDate", toISO(filters.endDate, true));
      if (filters.machineIds.length)
        p.set("machineIds", filters.machineIds.join(","));
      if (filters.shiftId) p.set("shiftId", filters.shiftId);
      p.set("performance", performance);
      p.set("quality", quality);
      const { data } = await axios.get(`${API_URL}/api/teep?${p}`, authH());
      if (data.success) setResult(data.data);
      else addToast(data.message || "Hesaplama başarısız", "danger");
    } catch (err) {
      addToast(err?.response?.data?.message || "Hesaplama başarısız", "danger");
    } finally {
      setLoading(false);
    }
  }, [
    filters.startDate,
    filters.endDate,
    filters.machineIds,
    filters.shiftId,
    performance,
    quality,
    authH,
    addToast,
  ]);

  useEffect(() => {
    runCalculation();
    // Initial load only; filter changes use ViewOptionsPanel "Uygula"
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pieData = result?.stopBreakdown || [];
  const reasonData = result?.topReasons?.slice(0, 10) || [];
  const metricsR = result
    ? {
        series: [
          result.metrics.availability,
          result.metrics.performance,
          result.metrics.quality,
          result.metrics.oee,
          result.metrics.teep,
        ],
        labels: ["Kullanılabilirlik", "Performans", "Kalite", "OEE", "TEEP"],
        colors: ["#3b82f6", "#f97316", "#22c55e", "#8b5cf6", "#ec4899"],
      }
    : null;

  const chartCommon = {
    chart: { fontFamily: "inherit", foreColor: "#333" },
    grid: { borderColor: "#f0f2f5" },
  };

  const perfReadOnly = result?.metrics?.performanceSource === "calculated";
  const qualReadOnly = result?.metrics?.qualitySource === "calculated";

  return (
    <AppPage
      title="TEEP Analizi"
      actions={
        <ReportViewOptionsPanel
          filters={filters}
          machines={machines}
          shifts={shifts}
          deviceModels={[]}
          onApply={runCalculation}
          showTransFilters={false}
          showMachines={machines.length > 0}
        />
      }
    >
      <div className="teep-report-page">
        <ToastContainer>
          {toasts.map((t) => (
            <Toast
              key={t.id}
              title={t.message}
              appearance={t.type === "danger" ? "danger" : "success"}
              onClose={() => dismissToast(t.id)}
            />
          ))}
        </ToastContainer>

        <div className="d-flex flex-column gap-3">
          {loading && <SectionSpinner />}

          {!loading && !result && (
            <SectionEmpty msg='Veri alınamadı. Filtreleri kontrol edip "Uygula" ile yenileyin.' />
          )}

          {result && (
            <>
              <WaterfallChart
                result={result}
                filters={filters}
                machines={machines}
              />
            </>
          )}
        </div>
      </div>
    </AppPage>
  );
};

export default withAuth(TEEP);
